import { useState } from "react";
import { Link } from "react-router-dom";
import { useContent, useSettings } from "@/lib/hooks";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import PolicyPage, { useSeo } from "@/components/PolicyPage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Mail, Phone, MapPin, MessageCircle, Clock, Building2 } from "lucide-react";

export function About() { return <PolicyPage contentKey="about" fallbackTitle="About Us" eyebrow="About" />; }
export function Privacy() { return <PolicyPage contentKey="privacy" fallbackTitle="Privacy Policy" />; }
export function Terms() { return <PolicyPage contentKey="terms" fallbackTitle="Terms & Conditions" />; }
export function Refund() { return <PolicyPage contentKey="refund" fallbackTitle="Refund & Cancellation Policy" />; }
export function Shipping() { return <PolicyPage contentKey="shipping" fallbackTitle="Shipping & Delivery Policy" />; }
export function DigitalDelivery() { return <PolicyPage contentKey="digital-delivery" fallbackTitle="Digital Products & Delivery Policy" />; }
export function Disclaimer() { return <PolicyPage contentKey="disclaimer" fallbackTitle="Disclaimer" />; }

export function FAQ() {
  const { data } = useContent("home");
  const faqs = data?.faq || [];
  useSeo("Frequently Asked Questions", "Answers to common questions about Water Business Guru products, payments and digital delivery.");
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
      <span className="eyebrow">Support</span>
      <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold text-navy">Frequently Asked Questions</h1>
      <Accordion type="single" collapsible className="w-full mt-6">
        {faqs.map((f, i) => (
          <AccordionItem key={i} value={`faq-${i}`}>
            <AccordionTrigger className="text-left font-medium text-navy">{f.q}</AccordionTrigger>
            <AccordionContent className="text-slate-600">{f.a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}

function BusinessInfo({ s, data }) {
  const addr = [s?.address, s?.operating_address, s?.city, s?.state, s?.pin_code, s?.country].filter(Boolean);
  const wa = (s?.whatsapp_number || "").replace(/[^0-9]/g, "");
  const phoneDigits = (s?.support_phone || "").replace(/[^0-9+]/g, "");
  return (
    <ul className="mt-6 space-y-3 text-sm">
      {(s?.legal_name || s?.business_name) && <li className="flex items-start gap-3 text-slate-700"><Building2 className="w-4 h-4 text-teal mt-0.5" /> <span><b>{s?.legal_name || s?.business_name}</b>{s?.legal_name && s?.business_name && s.legal_name !== s.business_name ? <span className="text-slate-500"> (brand: {s.business_name})</span> : null}</span></li>}
      {s?.support_email && <li className="flex items-center gap-3 text-slate-600"><Mail className="w-4 h-4 text-teal" /> <a href={`mailto:${s.support_email}`} className="hover:text-teal" data-testid="contact-email-link">{s.support_email}</a></li>}
      {s?.support_phone && <li className="flex items-center gap-3 text-slate-600"><Phone className="w-4 h-4 text-teal" /> <a href={`tel:${phoneDigits}`} className="hover:text-teal" data-testid="contact-call-link">{s.support_phone}</a></li>}
      {addr.length > 0 && <li className="flex items-start gap-3 text-slate-600"><MapPin className="w-4 h-4 text-teal mt-0.5" /> {addr.join(", ")}</li>}
      {s?.support_hours && <li className="flex items-center gap-3 text-slate-600"><Clock className="w-4 h-4 text-teal" /> {s.support_hours}</li>}
    </ul>
  );
}

export function Contact() {
  const { data } = useContent("contact");
  const { data: s } = useSettings();
  const wa = (s?.whatsapp_number || "").replace(/[^0-9]/g, "");
  const [form, setForm] = useState({ name: "", email: "", order_id: "", category: "Product", message: "" });
  const [loading, setLoading] = useState(false);
  useSeo(data?.title || "Contact Us", "Contact Water Business Guru for help with products, orders, downloads, invoices or refunds.");
  const submit = async () => {
    if (!form.name || !form.email || !form.message) return toast.error("Please fill name, email and message");
    setLoading(true);
    try { const { data: r } = await api.post("/support/tickets", form); toast.success(`Ticket ${r.ticket_no} created. We'll be in touch.`); setForm({ name: "", email: "", order_id: "", category: "Product", message: "" }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 grid md:grid-cols-5 gap-10" data-testid="contact-page">
      <div className="md:col-span-2">
        <span className="eyebrow">Contact</span>
        <h1 className="mt-2 text-3xl font-extrabold text-navy">{data?.title || "Contact Us"}</h1>
        <p className="mt-3 text-slate-500">{data?.intro || "Questions about a product, order or download? Send us a message."}</p>
        <BusinessInfo s={s} data={data} />
        <div className="mt-5 flex flex-wrap gap-2">
          {wa && (
            <a href={`https://wa.me/${wa}`} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp"
               className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium px-4 py-2.5 rounded-lg transition-colors">
              <MessageCircle className="w-4 h-4" /> Chat on WhatsApp
            </a>
          )}
        </div>
        <p className="mt-6 text-xs text-slate-400">For grievances and formal support requests, see our <Link to="/grievance" className="text-teal">Grievance &amp; Support</Link> page.</p>
      </div>
      <div className="md:col-span-3 bg-white border border-slate-200 rounded-xl p-6">
        <h2 className="font-heading font-semibold text-navy mb-3">Send us a message</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label className="text-xs">Name*</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" /></div>
          <div><Label className="text-xs">Email*</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" /></div>
          <div><Label className="text-xs">Order ID (optional)</Label><Input value={form.order_id} onChange={(e) => setForm({ ...form, order_id: e.target.value })} data-testid="contact-order" /></div>
          <div><Label className="text-xs">Category</Label>
            <select className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="contact-category">
              {["Payment", "Duplicate payment", "Download", "Product", "Order", "Shipping", "Refund", "Invoice", "Account", "Privacy / Data", "Other"].map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2"><Label className="text-xs">Message*</Label><Textarea rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message" /></div>
        </div>
        <Button className="mt-4 bg-teal hover:bg-teal-dark text-white" onClick={submit} disabled={loading} data-testid="contact-submit">Send message</Button>
      </div>
    </div>
  );
}

export function Grievance() {
  const { data } = useContent("grievance");
  const { data: s } = useSettings();
  const sections = data?.sections || [];
  useSeo(data?.title || "Grievance & Customer Support", "Raise payment, delivery, download, invoice, refund, account or privacy concerns with Water Business Guru.");
  const wa = (s?.whatsapp_number || "").replace(/[^0-9]/g, "");
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14" data-testid="policy-grievance">
      <span className="eyebrow">Support</span>
      <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold text-navy">{data?.title || "Grievance & Customer Support"}</h1>
      {data?.last_updated && <p className="mt-2 text-sm text-slate-400" data-testid="policy-updated">Last updated: {data.last_updated}</p>}
      <div className="mt-6 bg-white border border-slate-200 rounded-xl p-5">
        <h2 className="font-heading font-semibold text-navy mb-2">Contact details</h2>
        <BusinessInfo s={s} data={data} />
        {s?.grievance_officer && <p className="mt-3 text-sm text-slate-600">Grievance contact: <b>{s.grievance_officer}</b></p>}
        <div className="mt-4 flex flex-wrap gap-2">
          {s?.support_email && <a href={`mailto:${s.support_email}`} className="inline-flex items-center gap-2 bg-navy text-white text-sm px-4 py-2 rounded-lg"><Mail className="w-4 h-4" /> Email support</a>}
          {wa && <a href={`https://wa.me/${wa}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-emerald-600 text-white text-sm px-4 py-2 rounded-lg"><MessageCircle className="w-4 h-4" /> WhatsApp</a>}
          <Link to="/contact" className="inline-flex items-center gap-2 border border-slate-300 text-slate-700 text-sm px-4 py-2 rounded-lg">Open support form</Link>
        </div>
      </div>
      <div className="mt-8 space-y-6">
        {sections.map((sec, i) => (
          <section key={i}>
            {sec.heading && <h2 className="text-lg font-bold text-navy mb-1.5">{sec.heading}</h2>}
            <p className="text-slate-600 leading-relaxed whitespace-pre-line text-sm sm:text-base">{sec.body}</p>
          </section>
        ))}
      </div>
    </div>
  );
}
